import { useMemo } from 'react'
import { Picture } from 'src/components/ui/Image'
import { sanitizeHTML } from 'src/utils/sanitizeHTML'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'
import type { Product } from 'src/components/restructure/product/types/product'
import ProductCardV3 from 'src/components/restructure/product/ProductCardV3/ProductCardV3'
import './styles.scss'

import type { RevampStorefrontProps } from './types'

const REQUIRED_PRODUCTS = 4

const isProductAvailable = (product?: Product) =>
  Boolean(product?.items?.some((item) => item.isAvailable))

export default function RevampStorefront({
  enableSession,
  theme = 'light',
  customBackgroundColor,
  customTextColor,
  image,
  title,
  subtitle,
  products,
  shelfServerData,
  showOnDesktop,
  showOnMobile,
}: Readonly<RevampStorefrontProps>) {
  const hasCustomBackground = Boolean(customBackgroundColor?.trim())

  const themeClass =
    theme === 'dark'
      ? 'bg-restructure-background-primary-inverted text-white'
      : 'bg-white text-restructure-primary'

  const overlayTextColor = customTextColor?.trim()
    ? customTextColor.trim()
    : theme === 'dark'
    ? '#FFFFFF'
    : '#111827'

  const customStyle = useMemo(() => {
    const style: { backgroundColor?: string; color?: string } = {}

    if (hasCustomBackground) {
      style.backgroundColor = customBackgroundColor?.trim()
    }

    if (customTextColor?.trim()) {
      style.color = customTextColor.trim()
    }

    return style
  }, [hasCustomBackground, customBackgroundColor, customTextColor])

  const visibilityClasses = useMemo(
    () => getResponsiveVisibility(showOnDesktop, showOnMobile),
    [showOnDesktop, showOnMobile]
  )

  const hasAllProductIds =
    Boolean(products) &&
    Object.values(products).filter((id) => Boolean(id?.trim())).length ===
      REQUIRED_PRODUCTS

  const validProducts = useMemo(
    () => (shelfServerData ?? []).filter(isProductAvailable),
    [shelfServerData]
  )

  const safeTitle = title ? sanitizeHTML(title) : ''
  const safeSubtitle = subtitle ? sanitizeHTML(subtitle) : ''

  if (!enableSession) {
    return null
  }

  if (!image?.imageDesktop || !image?.imageMobile) {
    return null
  }

  if (!hasAllProductIds || validProducts.length === 0) {
    return null
  }

  return (
    <section
      data-testid="section-revamp-storefront"
      aria-label="Vitrine editorial comercial"
      style={customStyle}
      className={`max-w-[1440px] mx-auto w-full px-4 pt-[56px] pb-[36px] md:px-0 restructure-small-desktop:py-[69px] ${visibilityClasses} ${
        hasCustomBackground ? '' : themeClass
      }`}
    >
      <div className="flex flex-col gap-4 restructure-small-desktop:flex-row restructure-small-desktop:justify-center restructure-small-desktop:gap-[54px]">
        <div className="relative h-[460px] aspect-[390/466] w-full overflow-hidden md:h-[859px] restructure-small-desktop:aspect-[656/875] restructure-small-desktop:w-[45.556%] restructure-small-desktop:shrink-0 restructure-small-desktop:self-start">
          <Picture
            className="absolute inset-0 h-full w-full object-cover"
            sources={[
              {
                src: image.imageMobile,
                width: 390,
                height: 466,
                media: '(max-width: 1023px)',
              },
              {
                src: image.imageDesktop,
                width: 656,
                height: 859,
                media: '(min-width: 1024px)',
              },
            ]}
            img={{
              src: image.imageDesktop,
              width: 656,
              height: 859,
              'data-id': image.imageDesktop,
              title: image.alt,
              alt: image.alt,
              loading: 'lazy',
            }}
            shouldCompress={false}
          />

          {(safeTitle || safeSubtitle) && (
            <>
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
              <div
                className="absolute bottom-0 left-0 z-10 flex flex-col gap-1 p-5"
                style={{ color: overlayTextColor }}
              >
                {safeTitle && (
                  <div
                    className="font-inter text-2xl font-bold uppercase leading-7 restructure-small-desktop:text-[28px] restructure-small-desktop:leading-8"
                    dangerouslySetInnerHTML={{ __html: safeTitle }}
                  />
                )}
                {safeSubtitle && (
                  <div
                    className="font-inter text-sm leading-5 opacity-90 restructure-small-desktop:text-base"
                    dangerouslySetInnerHTML={{ __html: safeSubtitle }}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <ul className="revamp-storefront-product-overrides grid w-full min-w-0 list-none grid-cols-2 gap-3 restructure-small-desktop:w-auto restructure-small-desktop:self-start restructure-small-desktop:grid-cols-[repeat(2,minmax(0,332.57px))] restructure-small-desktop:gap-[22px]">
          {validProducts.slice(0, REQUIRED_PRODUCTS).map((product, index) => (
            <li key={product.productId} className="flex">
              <ProductCardV3
                product={product}
                index={index}
                shelfRef="Vitrine Editorial Comercial"
                shelfEngine="editorial-comercial"
                title="Vitrine Editorial Comercial"
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
